import { useParams, Link } from "wouter";
import { endToolSession } from "@/lib/usageGate";
import { DownloadGateRegistrar } from "@/components/DownloadGateRegistrar";
import { dropHandoffUnless } from "@/lib/handoff";
import { getToolById, getCategoryById } from "@/lib/tools-data";
import { getToolName } from "@/lib/i18n/tool-name-translations";
import { getCategoryName } from "@/lib/i18n/category-persona-translations";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { lazy, Suspense, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { UsageAndUpgradeStrip } from "@/components/UsageAndUpgradeStrip";
import { AfterDownloadAd, InlineAd, SideRailAd, ToolResultAd } from "@/components/AdBanner";
import { AUTO_ADS_EXCLUDE_CLASS } from "@/lib/adPlacement";
import { TOOL_SKELETON_RESERVE_CLASS } from "@/lib/layoutReserve";
import { ToolEngagementFooter } from "@/components/ToolEngagementFooter";
import { FavoriteButton } from "@/components/FavoriteButton";
import { ShareButton } from "@/components/ShareButton";
import { InstallToolButton } from "@/components/InstallToolButton";
import { RegionBadge } from "@/components/RegionBadge";
import { ToolTitleBadges } from "@/components/ToolTitleBadges";
import { ToolInfoPanel } from "@/components/ToolInfoPanel";
import { useToolInfoState } from "@/components/ToolInfoContext";
import { FEATURED_APP_IDS, FEATURED_APP_CONTENT } from "@/lib/featured-apps-data";
import { isWorkflowProduct, isPremiumApp } from "@/lib/tiers";
import { FeaturedAppHero } from "@/components/FeaturedAppHero";
import { TOOL_EDITORIAL_CONTENT } from "@/lib/tool-editorial-content";
import { usePwaManifest } from "@/hooks/usePwaManifest";
import { useLocale } from "@/hooks/useLocale";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getServerLocaleCopy, applyServerFaq } from "@/lib/serverLocaleCopy";
import { useStructuredData } from "@/hooks/useStructuredData";
import { useRecentTools } from "@/hooks/useFavorites";
import { getFaqForTool } from "@/lib/faq-content";
import { pairBySlug, pairTitle, pairMeta, pairH1 } from "@/tools/converters/unitPairs";
import { getSeoTitle, getSeoH1, getToolMetaDescription } from "@/lib/seo-titles";
import { getToolVideo, videoDisplayTitle } from "@/lib/tool-videos";

// Lazy-load tool implementations. Exported so PipelineRunnerPage can
// render any tool's real component by ID too, rather than maintaining
// a second, easy-to-drift copy of this same ~200-entry map.
export const toolComponents: Record<
  string,
  React.LazyExoticComponent<React.ComponentType>
> = {
  // PDF Tools
  "merge-pdf": lazy(() => import("@/tools/pdf/MergePdf")),
  "split-pdf": lazy(() => import("@/tools/pdf/SplitPdf")),
  "compress-pdf": lazy(() => import("@/tools/pdf/CompressPdf")),
  "rotate-pdf": lazy(() => import("@/tools/pdf/RotatePdf")),
  "pdf-to-word": lazy(() => import("@/tools/pdf/PdfToWord")),
  "pdf-to-jpg": lazy(() => import("@/tools/pdf/PdfToJpg")),
  "pdf-to-excel": lazy(() => import("@/tools/pdf/PdfToExcel")),
  "word-to-pdf": lazy(() => import("@/tools/pdf/WordToPdf")),
  "excel-to-pdf": lazy(() => import("@/tools/pdf/ExcelToPdf")),
  "powerpoint-to-pdf": lazy(() => import("@/tools/pdf/PowerPointToPdf")),
  "powerpoint-to-images": lazy(() => import("@/tools/slides/PowerPointToImages")),
  "extract-from-powerpoint": lazy(() => import("@/tools/slides/ExtractFromPowerPoint")),
  "clean-powerpoint": lazy(() => import("@/tools/slides/CleanPowerPoint")),
  "images-to-powerpoint": lazy(() => import("@/tools/slides/ImagesToPowerPoint")),
  "markdown-to-powerpoint": lazy(() => import("@/tools/slides/MarkdownToPowerPoint")),
  "pdf-to-powerpoint": lazy(() => import("@/tools/slides/PdfToPowerPoint")),
  "split-powerpoint": lazy(() => import("@/tools/slides/SplitPowerPoint")),
  "compress-powerpoint": lazy(() => import("@/tools/slides/CompressPowerPoint")),
  "organize-slides": lazy(() => import("@/tools/slides/OrganizeSlides")),
  "merge-powerpoint": lazy(() => import("@/tools/slides/MergePowerPoint")),
  "find-replace-powerpoint": lazy(() => import("@/tools/slides/FindReplacePowerPoint")),
  "replace-fonts-powerpoint": lazy(() => import("@/tools/slides/ReplaceFontsPowerPoint")),
  "add-slide-numbers": lazy(() => import("@/tools/slides/SlideNumbersPowerPoint")),
  "watermark-powerpoint": lazy(() => import("@/tools/slides/WatermarkPowerPoint")),
  "resize-slides": lazy(() => import("@/tools/slides/ResizeSlides")),
  "accessibility-checker-powerpoint": lazy(() => import("@/tools/slides/AccessibilityCheckerPowerPoint")),
  "extract-charts-powerpoint": lazy(() => import("@/tools/slides/ExtractChartsPowerPoint")),
  "word-to-powerpoint": lazy(() => import("@/tools/slides/WordToPowerPoint")),
  "excel-to-powerpoint": lazy(() => import("@/tools/slides/ExcelToPowerPoint")),
  "slide-deck-maker": lazy(() => import("@/tools/slides/SlideDeckMaker")),
  "photo-slideshow-maker": lazy(() => import("@/tools/slides/PhotoSlideshowMaker")),
  "repair-powerpoint": lazy(() => import("@/tools/slides/RepairPowerPoint")),
  "redact-powerpoint": lazy(() => import("@/tools/slides/RedactPowerPoint")),
  "powerpoint-to-html": lazy(() => import("@/tools/slides/PowerPointToHtml")),
  "powerpoint-to-video": lazy(() => import("@/tools/slides/PowerPointToVideo")),
  "powerpoint-to-gif": lazy(() => import("@/tools/slides/PowerPointToGif")),
  "powerpoint-templates": lazy(() => import("@/tools/slides/PowerPointTemplates")),
  "template-theme-maker": lazy(() => import("@/tools/slides/TemplateThemeMaker")),
  "password-protect-powerpoint": lazy(() => import("@/tools/slides/PasswordProtectPowerPoint")),
  "presentation-studio": lazy(() => import("@/tools/slides/PresentationStudio")),
  "image-to-pdf": lazy(() => import("@/tools/pdf/ImageToPdf")),
  "pdf-password": lazy(() => import("@/tools/pdf/PdfPassword")),
  "pdf-watermark": lazy(() => import("@/tools/pdf/PdfWatermark")),
  "extract-pdf": lazy(() => import("@/tools/pdf/ExtractPdf")),
  "organize-pdf": lazy(() => import("@/tools/pdf/OrganizePdf")),
  "add-page-numbers": lazy(() => import("@/tools/pdf/AddPageNumbers")),
  "legal-production-desk": lazy(() => import("@/tools/pdf/LegalProductionDesk")),
  "exhibit-stamper": lazy(() => import("@/tools/pdf/ExhibitStamper")),
  "bates-numbering": lazy(() => import("@/tools/pdf/BatesNumbering")),
  "book-writing-toolbox": lazy(() => import("@/tools/pdf/BookMaker")),
  "magazine-maker": lazy(() => import("@/tools/pdf/MagazineMaker")),
  "catalog-maker": lazy(() => import("@/tools/pdf/CatalogMaker")),
  "document-assembler": lazy(() => import("@/tools/pdf/DocumentAssembler")),
  "redact-pdf": lazy(() => import("@/tools/pdf/RedactPdf")),
  "ocr-pdf": lazy(() => import("@/tools/pdf/OcrPdf")),
  "html-to-pdf": lazy(() => import("@/tools/pdf/HtmlToPdf")),
  "fillable-forms": lazy(() => import("@/tools/pdf/FillableForms")),
  "flatten-pdf": lazy(() => import("@/tools/pdf/FlattenPdf")),
  "compare-pdf": lazy(() => import("@/tools/pdf/ComparePdf")),
  "repair-pdf": lazy(() => import("@/tools/pdf/RepairPdf")),
  "crop-pdf": lazy(() => import("@/tools/pdf/CropPdf")),
  "pdf-editor": lazy(() => import("@/tools/pdf/PdfEditor")),
  "sign-pdf": lazy(() => import("@/tools/pdf/SignPdf")),
  // Image Tools
  "compress-image": lazy(() => import("@/tools/image/CompressImage")),
  "resize-crop": lazy(() => import("@/tools/image/ResizeCrop")),
  "convert-image": lazy(() => import("@/tools/image/ConvertImage")),
  "remove-background": lazy(() => import("@/tools/image/RemoveBackground")),
  "flip-rotate-image": lazy(() => import("@/tools/image/FlipRotateImage")),
  "image-filters": lazy(() => import("@/tools/image/ImageFilters")),
  "image-quality-enhancer": lazy(() => import("@/tools/image/ImageQualityEnhancer")),
  "document-scanner": lazy(() => import("@/tools/image/DocumentScanner")),
  "camera-capture": lazy(() => import("@/tools/image/CameraCapture")),
  // "images-to-pdf" removed -- redirects to "image-to-pdf" (see App.tsx),
  // no longer rendered directly.
  "bulk-image": lazy(() => import("@/tools/image/BulkImage")),
  "webp-converter": lazy(() => import("@/tools/image/WebpConverter")),
  "passport-photo": lazy(() => import("@/tools/image/PassportPhoto")),
  "screenshot-tool": lazy(() => import("@/tools/image/ScreenshotTool")),
  "collage-maker": lazy(() => import("@/tools/image/CollageMaker")),
  // Video Tools
  "compress-video": lazy(() => import("@/tools/video/CompressVideo")),
  "convert-video": lazy(() => import("@/tools/video/ConvertVideo")),
  "trim-video": lazy(() => import("@/tools/video/TrimVideo")),
  "merge-video": lazy(() => import("@/tools/video/MergeVideo")),
  "video-to-gif": lazy(() => import("@/tools/video/VideoToGif")),
  "extract-audio": lazy(() => import("@/tools/audio/ExtractAudio")),
  "audio-trimmer": lazy(() => import("@/tools/audio/AudioTrimmer")),
  "audio-converter": lazy(() => import("@/tools/audio/AudioConverter")),
  "audio-merger": lazy(() => import("@/tools/audio/AudioMerger")),
  "audio-compressor": lazy(() => import("@/tools/audio/AudioCompressor")),
  "voice-recorder": lazy(() => import("@/tools/audio/VoiceRecorder")),
  "audio-speed-changer": lazy(() => import("@/tools/audio/AudioSpeedChanger")),
  "silence-remover": lazy(() => import("@/tools/audio/SilenceRemover")),
  "acx-audiobook-checker": lazy(() => import("@/tools/audio/AcxChecker")),
  "bank-statement-to-csv": lazy(() => import("@/tools/business/BankStatementToCsv")),
  "blind-cv-redactor": lazy(() => import("@/tools/business/BlindCvRedactor")),
  "audio-to-subtitles": lazy(() => import("@/tools/audio/subtitles/AudioToSubtitles")),
  "resume-keyword-matcher": lazy(() => import("@/tools/business/ResumeKeywordMatcher")),
  "open-house-sign-in-sheet": lazy(() => import("@/tools/business/OpenHouseSignIn")),
  "mail-merge-pdf": lazy(() => import("@/tools/file/MailMergePdf")),
  "digital-download-packager": lazy(() => import("@/tools/ecommerce/DigitalDownloadPackager")),
  "low-content-interior-maker": lazy(() => import("@/tools/book/LowContentInteriorMaker")),
  "audio-normalizer": lazy(() => import("@/tools/audio/AudioNormalizer")),
  "audio-comparer": lazy(() => import("@/tools/audio/CompareAudio")),
  "noise-remover": lazy(() => import("@/tools/audio/NoiseRemover")),
  "audio-scrambler": lazy(() => import("@/tools/audio/AudioScrambler")),
  "thumbnail-gen": lazy(() => import("@/tools/video/ThumbnailGen")),
  "subtitle-adder": lazy(() => import("@/tools/video/SubtitleAdder")),
  "screen-recorder": lazy(() => import("@/tools/video/ScreenRecorder")),
  // Text Tools
  "word-counter": lazy(() => import("@/tools/text/WordCounter")),
  "case-converter": lazy(() => import("@/tools/text/CaseConverter")),
  "text-paraphraser": lazy(() => import("@/tools/text/TextParaphraser")),
  "grammar-checker": lazy(() => import("@/tools/text/GrammarChecker")),
  "lorem-ipsum": lazy(() => import("@/tools/text/LoremIpsum")),
  "find-replace": lazy(() => import("@/tools/text/FindReplace")),
  "text-reverser": lazy(() => import("@/tools/text/TextReverser")),
  "text-to-speech": lazy(() => import("@/tools/text/TextToSpeech")),
  "password-generator": lazy(() => import("@/tools/text/PasswordGenerator")),
  "fancy-font-generator": lazy(() => import("@/tools/text/FancyFontGenerator")),
  "invisible-text-generator": lazy(
    () => import("@/tools/text/InvisibleTextGenerator")
  ),
  "text-sorter": lazy(() => import("@/tools/text/TextSorter")),
  "email-extractor": lazy(() => import("@/tools/text/EmailExtractor")),
  "url-extractor": lazy(() => import("@/tools/text/UrlExtractor")),
  "slug-generator": lazy(() => import("@/tools/text/SlugGenerator")),
  "reading-level-checker": lazy(
    () => import("@/tools/text/ReadingLevelChecker")
  ),
  "ascii-art-generator": lazy(() => import("@/tools/text/AsciiArtGenerator")),
  "markdown-editor": lazy(() => import("@/tools/text/MarkdownEditor")),
  "markdown-html-converter": lazy(
    () => import("@/tools/text/MarkdownHtmlConverter")
  ),
  // Converters & Calculators
  "unit-converter": lazy(() => import("@/tools/converters/UnitConverter")),
  "heic-to-jpg": lazy(() => import("@/tools/converters/HeicToJpg")),
  "universal-file-converter": lazy(() => import("@/tools/converters/UniversalFileConverter")),
  "currency-converter": lazy(
    () => import("@/tools/converters/CurrencyConverter")
  ),
  "timezone-converter": lazy(
    () => import("@/tools/converters/TimezoneConverter")
  ),
  "loan-calculator": lazy(() => import("@/tools/converters/LoanCalculator")),
  "bmi-calculator": lazy(() => import("@/tools/converters/BmiCalculator")),
  "tip-calculator": lazy(() => import("@/tools/converters/TipCalculator")),
  "online-timer": lazy(() => import("@/tools/utilities/OnlineTimer")),
  "age-calculator": lazy(() => import("@/tools/converters/AgeCalculator")),
  "job-experience-calculator": lazy(
    () => import("@/tools/converters/JobExperienceCalculator")
  ),
  "recipe-scaler": lazy(() => import("@/tools/converters/RecipeScaler")),
  "percentage-calculator": lazy(
    () => import("@/tools/converters/PercentageCalculator")
  ),
  "gpa-calculator": lazy(() => import("@/tools/converters/GpaCalculator")),
  "roman-numeral-converter": lazy(
    () => import("@/tools/converters/RomanNumeralConverter")
  ),
  "random-number-generator": lazy(
    () => import("@/tools/converters/RandomNumberGenerator")
  ),
  "discount-calculator": lazy(
    () => import("@/tools/converters/DiscountCalculator")
  ),
  "compound-interest": lazy(
    () => import("@/tools/converters/CompoundInterestCalculator")
  ),
  "mortgage-calculator": lazy(
    () => import("@/tools/converters/MortgageCalculator")
  ),
  "paycheck-calculator": lazy(
    () => import("@/tools/converters/PaycheckCalculator")
  ),
  "gst-calculator": lazy(() => import("@/tools/converters/GstCalculator")),
  "sip-calculator": lazy(() => import("@/tools/converters/SipCalculator")),
  "body-fat-calculator": lazy(
    () => import("@/tools/converters/BodyFatCalculator")
  ),
  "bmr-macro-calculator": lazy(
    () => import("@/tools/converters/BmrMacroCalculator")
  ),
  "calorie-calculator": lazy(
    () => import("@/tools/converters/CalorieCalculator")
  ),
  "ideal-weight-calculator": lazy(
    () => import("@/tools/converters/IdealWeightCalculator")
  ),
  "pregnancy-due-date": lazy(
    () => import("@/tools/converters/PregnancyDueDateCalculator")
  ),
  "period-calculator": lazy(
    () => import("@/tools/converters/PeriodCalculator")
  ),
  "pregnancy-scan-calculator": lazy(
    () => import("@/tools/converters/PregnancyScanCalculator")
  ),
  "water-intake-calculator": lazy(
    () => import("@/tools/converters/WaterIntakeCalculator")
  ),
  "sales-tax-calculator": lazy(
    () => import("@/tools/converters/SalesTaxCalculator")
  ),
  "vat-calculator": lazy(() => import("@/tools/converters/VatCalculator")),
  "self-employment-tax": lazy(
    () => import("@/tools/converters/SelfEmploymentTaxCalculator")
  ),
  "state-tax-calculator": lazy(
    () => import("@/tools/converters/StateTaxCalculator")
  ),
  "income-tax-calculator": lazy(
    () => import("@/tools/converters/IncomeTaxCalculator")
  ),
  "india-income-tax": lazy(
    () => import("@/tools/converters/IndiaIncomeTaxCalculator")
  ),
  "uk-tax-calculator": lazy(() => import("@/tools/converters/UkTaxCalculator")),
  "social-security-calculator": lazy(
    () => import("@/tools/converters/SocialSecurityCalculator")
  ),
  "401k-calculator": lazy(
    () => import("@/tools/converters/FourOhOneKCalculator")
  ),
  "rmd-calculator": lazy(() => import("@/tools/converters/RmdCalculator")),
  "fire-calculator": lazy(() => import("@/tools/converters/FireCalculator")),
  // QR Tools
  "qr-generator": lazy(() => import("@/tools/qr/QrGenerator")),
  "custom-qr": lazy(() => import("@/tools/qr/CustomQr")),
  "bulk-qr": lazy(() => import("@/tools/qr/BulkQr")),
  "qr-scanner": lazy(() => import("@/tools/qr/QrScanner")),
  "barcode-generator": lazy(() => import("@/tools/qr/BarcodeGenerator")),
  // Business Tools
  "resume-builder": lazy(() => import("@/tools/business/ResumeBuilder")),
  "invoice-generator": lazy(() => import("@/tools/business/InvoiceGenerator")),
  "business-card": lazy(() => import("@/tools/business/BusinessCard")),
  "contract-templates": lazy(
    () => import("@/tools/business/ContractTemplates")
  ),
  "expense-tracker": lazy(() => import("@/tools/business/ExpenseTracker")),
  "receipt-maker": lazy(() => import("@/tools/business/ReceiptMaker")),
  "government-forms-hub": lazy(
    () => import("@/tools/business/GovernmentFormsHub")
  ),
  "cover-letter-maker": lazy(() => import("@/tools/business/CoverLetterMaker")),
  "pay-stub-generator": lazy(() => import("@/tools/business/PayStubGenerator")),
  "proposal-builder": lazy(() => import("@/tools/business/ProposalBuilder")),
  "rental-application-maker": lazy(() => import("@/tools/business/RentalApplicationMaker")),
  "nda-generator": lazy(() => import("@/tools/business/NdaGenerator")),
  "lease-agreement-generator": lazy(
    () => import("@/tools/business/LeaseAgreementGenerator")
  ),
  "power-of-attorney-generator": lazy(
    () => import("@/tools/business/PowerOfAttorneyGenerator")
  ),
  "bill-of-sale-generator": lazy(
    () => import("@/tools/business/BillOfSaleGenerator")
  ),
  "promissory-note-generator": lazy(
    () => import("@/tools/business/PromissoryNoteGenerator")
  ),
  "eviction-notice-generator": lazy(
    () => import("@/tools/business/EvictionNoticeGenerator")
  ),
  "startup-name-generator": lazy(
    () => import("@/tools/business/StartupNameGenerator")
  ),
  "employment-offer-letter": lazy(
    () => import("@/tools/business/EmploymentOfferLetterGenerator")
  ),
  "product-description-writer": lazy(
    () => import("@/tools/business/ProductDescriptionWriter")
  ),
  "user-manual-writer": lazy(() => import("@/tools/business/UserManualWriter")),
  "invention-disclosure-worksheet": lazy(
    () => import("@/tools/business/InventionDisclosureWorksheet")
  ),
  // Design Tools
  "color-palette": lazy(() => import("@/tools/design/ColorPalette")),
  "color-converter": lazy(() => import("@/tools/design/ColorConverter")),
  "gradient-generator": lazy(() => import("@/tools/design/GradientGenerator")),
  "favicon-generator": lazy(() => import("@/tools/design/FaviconGenerator")),
  "logo-maker": lazy(() => import("@/tools/design/LogoMaker")),
  "mockup-generator": lazy(() => import("@/tools/design/MockupGenerator")),
  "image-to-svg": lazy(() => import("@/tools/design/ImageToSvg")),
  "color-picker-image": lazy(() => import("@/tools/design/ColorPickerImage")),
  "social-media-formatter": lazy(
    () => import("@/tools/design/SocialMediaFormatter")
  ),
  "box-shadow-generator": lazy(
    () => import("@/tools/design/BoxShadowGenerator")
  ),
  "border-radius-generator": lazy(
    () => import("@/tools/design/BorderRadiusGenerator")
  ),
  "contrast-checker": lazy(() => import("@/tools/design/ContrastChecker")),
  "clip-path-generator": lazy(() => import("@/tools/design/ClipPathGenerator")),
  "font-pairing-tool": lazy(() => import("@/tools/design/FontPairingTool")),
  // Security Tools
  "password-strength": lazy(() => import("@/tools/security/PasswordStrength")),
  base64: lazy(() => import("@/tools/security/Base64Tool")),
  "json-formatter": lazy(() => import("@/tools/security/JsonFormatter")),
  "regex-tester": lazy(() => import("@/tools/security/RegexTester")),
  "url-encoder": lazy(() => import("@/tools/security/UrlEncoder")),
  "hash-generator": lazy(() => import("@/tools/security/HashGenerator")),
  "jwt-decoder": lazy(() => import("@/tools/security/JwtDecoder")),
  "paper-submission-desk": lazy(() => import("@/tools/college/PaperSubmissionDesk")),
  "brand-kit": lazy(() => import("@/tools/design/BrandKit")),
  "site-launch-kit": lazy(() => import("@/tools/seo/SiteLaunchKit")),
  "seller-studio": lazy(() => import("@/tools/ecommerce/SellerStudio")),
  "seller-pricing-desk": lazy(() => import("@/tools/ecommerce/SellerPricingDesk")),
  "kdp-description-formatter": lazy(() => import("@/tools/book/KdpDescriptionFormatter")),
  "print-royalty-calculator": lazy(() => import("@/tools/book/PrintRoyaltyCalculator")),
  "etsy-listing-checker": lazy(() => import("@/tools/ecommerce/EtsyListingChecker")),
  "label-sheet-maker": lazy(() => import("@/tools/business/LabelSheetMaker")),
  "listing-photo-pack": lazy(() => import("@/tools/ecommerce/ListingPhotoPack")),
  "email-signature-generator": lazy(() => import("@/tools/business/EmailSignatureGenerator")),
  "final-grade-calculator": lazy(() => import("@/tools/converters/FinalGradeCalculator")),
  "packing-slip-generator": lazy(() => import("@/tools/business/PackingSlipGenerator")),
  "purchase-order-generator": lazy(() => import("@/tools/business/PurchaseOrderGenerator")),
  "quote-generator": lazy(() => import("@/tools/business/QuoteGenerator")),
  "volume-booster": lazy(() => import("@/tools/audio/VolumeBooster")),
  "ringtone-maker": lazy(() => import("@/tools/audio/RingtoneMaker")),
  "reverse-video": lazy(() => import("@/tools/video/ReverseVideo")),
  "video-speed-changer": lazy(() => import("@/tools/video/VideoSpeed")),
  "rotate-video": lazy(() => import("@/tools/video/RotateVideo")),
  "mute-video": lazy(() => import("@/tools/video/MuteVideo")),
  "image-splitter": lazy(() => import("@/tools/image/ImageSplitter")),
  "exif-remover": lazy(() => import("@/tools/image/ExifRemover")),
  "remove-pdf-pages": lazy(() => import("@/tools/pdf/RemovePdfPages")),
  "unlock-pdf": lazy(() => import("@/tools/pdf/UnlockPdf")),
  "string-escape": lazy(() => import("@/tools/dev/StringEscape")),
  "book-interior-maker": lazy(() => import("@/tools/book/BookInteriorMaker")),
  "book-page-estimator": lazy(() => import("@/tools/book/BookPageEstimator")),
  "book-spine-calculator": lazy(() => import("@/tools/book/BookSpineCalculator")),
  "copyright-page-maker": lazy(() => import("@/tools/book/CopyrightPageMaker")),
  "epub-checker": lazy(() => import("@/tools/book/EpubChecker")),
  "epub-maker": lazy(() => import("@/tools/book/EpubMaker")),
  "isbn-checker": lazy(() => import("@/tools/book/IsbnChecker")),
  "manuscript-formatter": lazy(() => import("@/tools/book/ManuscriptFormatter")),
  "onix-generator": lazy(() => import("@/tools/book/OnixGenerator")),
  "pdf-bookmarks-editor": lazy(() => import("@/tools/book/PdfBookmarksEditor")),
  "chmod-calculator": lazy(() => import("@/tools/dev/ChmodCalculator")),
  "subnet-calculator": lazy(() => import("@/tools/dev/SubnetCalculator")),
  "curl-converter": lazy(() => import("@/tools/dev/CurlConverter")),
  "sql-formatter": lazy(() => import("@/tools/dev/SqlFormatter")),
  "morse-code-translator": lazy(() => import("@/tools/text/MorseCode")),
  "number-to-words": lazy(() => import("@/tools/text/NumberToWords")),
  "text-cleaner": lazy(() => import("@/tools/text/TextCleaner")),
  "uuid-generator": lazy(() => import("@/tools/security/UuidGenerator")),
  "diff-checker": lazy(() => import("@/tools/security/DiffChecker")),
  "binary-ascii-converter": lazy(
    () => import("@/tools/security/BinaryAsciiConverter")
  ),
  "timestamp-converter": lazy(
    () => import("@/tools/security/TimestampConverter")
  ),
  "cron-builder": lazy(() => import("@/tools/security/CronBuilder")),
  "http-status-reference": lazy(
    () => import("@/tools/security/HttpStatusReference")
  ),
  "html-entity-converter": lazy(
    () => import("@/tools/security/HtmlEntityConverter")
  ),
  "css-js-minifier": lazy(() => import("@/tools/security/CssJsMinifier")),
  "user-agent-parser": lazy(() => import("@/tools/security/UserAgentParser")),
  // SEO Tools
  "meta-tag-gen": lazy(() => import("@/tools/seo/MetaTagGen")),
  "keyword-density": lazy(() => import("@/tools/seo/KeywordDensity")),
  "hashtag-generator": lazy(() => import("@/tools/seo/HashtagGenerator")),
  "caption-generator": lazy(() => import("@/tools/seo/CaptionGenerator")),
  "robots-sitemap": lazy(() => import("@/tools/seo/RobotsSitemap")),
  "og-preview": lazy(() => import("@/tools/seo/OgPreview")),
  "schema-generator": lazy(() => import("@/tools/seo/SchemaGenerator")),
  // File Tools
  "zip-unzip": lazy(() => import("@/tools/file/ZipUnzip")),
  "json-csv-converter": lazy(() => import("@/tools/file/JsonCsvConverter")),
  "csv-converter": lazy(() => import("@/tools/file/CsvConverter")),
  "meme-generator": lazy(() => import("@/tools/file/MemeGenerator")),
  "ocr-tool": lazy(() => import("@/tools/file/OcrTool")),
  "file-size-converter": lazy(() => import("@/tools/file/FileSizeConverter")),
  "yt-thumbnail": lazy(() => import("@/tools/file/YtThumbnail")),
  // 3D & CAD Files
  "mesh-3d-converter": lazy(() => import("@/tools/3d/Mesh3DConverter")),
  "stl-ascii-binary-converter": lazy(
    () => import("@/tools/3d/StlAsciiBinaryConverter")
  ),
  "mesh-3d-viewer": lazy(() => import("@/tools/3d/Mesh3DViewer")),
  // E-commerce
  "profit-margin-calculator": lazy(
    () => import("@/tools/ecommerce/ProfitMarginCalculator")
  ),
  "marketplace-fee-calculator": lazy(
    () => import("@/tools/ecommerce/MarketplaceFeeCalculator")
  ),
  "shipping-cost-estimator": lazy(
    () => import("@/tools/ecommerce/ShippingCostEstimator")
  ),
  "break-even-calculator": lazy(
    () => import("@/tools/ecommerce/BreakEvenCalculator")
  ),
  // Travel Planning
  "travel-budget-planner": lazy(
    () => import("@/tools/travel/TravelBudgetPlanner")
  ),
  "travel-itinerary-planner": lazy(
    () => import("@/tools/travel/TravelItineraryPlanner")
  ),
  "travel-packing-list": lazy(() => import("@/tools/travel/TravelPackingList")),
  "travel-countdown-planner": lazy(
    () => import("@/tools/travel/TravelCountdownPlanner")
  ),
  // Event Planning
  "event-budget-planner": lazy(
    () => import("@/tools/events/EventBudgetPlanner")
  ),
  "event-checklist-planner": lazy(
    () => import("@/tools/events/EventChecklistPlanner")
  ),
  "guest-list-tracker": lazy(() => import("@/tools/events/GuestListTracker")),
  "name-badge-maker": lazy(() => import("@/tools/events/NameBadgeMaker")),
  "business-days-calculator": lazy(() => import("@/tools/converters/BusinessDaysCalculator")),
  "certificate-maker": lazy(() => import("@/tools/events/CertificateMaker")),
  "dietary-access-summary": lazy(() => import("@/tools/events/DietaryAccessSummary")),
  "calendar-invite-maker": lazy(() => import("@/tools/events/CalendarInviteMaker")),
  "event-landing-page-generator": lazy(() => import("@/tools/events/EventLandingPageGenerator")),
  "catering-quantity-calculator": lazy(() => import("@/tools/events/CateringQuantityCalculator")),
  "event-profit-ledger": lazy(() => import("@/tools/events/EventProfitLedger")),
  "receipt-to-expense-sheet": lazy(() => import("@/tools/events/ReceiptToExpenseSheet")),
  "timesheet-generator": lazy(() => import("@/tools/events/TimesheetGenerator")),
  "event-closure-report-maker": lazy(() => import("@/tools/events/EventClosureReportMaker")),
  "booth-layout-planner": lazy(() => import("@/tools/events/BoothLayoutPlanner")),
  "exhibitor-booth-tracker": lazy(() => import("@/tools/events/ExhibitorBoothTracker")),
  "visitor-lead-capture-sheet": lazy(() => import("@/tools/events/VisitorLeadCaptureSheet")),
  "merchandise-order-sheet": lazy(() => import("@/tools/events/MerchandiseOrderSheet")),
  "badge-pass-maker": lazy(() => import("@/tools/events/BadgePassMaker")),
  "invite-calendar-maker": lazy(() => import("@/tools/events/InviteCalendarMaker")),
  "expense-capture-sheet": lazy(() => import("@/tools/events/ExpenseCaptureSheet")),
  "agenda-cue-sheet-builder": lazy(() => import("@/tools/events/AgendaCueSheetBuilder")),
  "rooming-dietary-sheet": lazy(() => import("@/tools/events/RoomingDietarySheet")),
  "vendor-quote-comparison": lazy(() => import("@/tools/events/VendorQuoteComparison")),
  "quote-comparison-builder": lazy(() => import("@/tools/events/QuoteComparisonBuilder")),
  "guest-manager": lazy(() => import("@/tools/events/GuestManager")),
  "invitation-automator": lazy(() => import("@/tools/events/InvitationAutomator")),
  "programme-show-day-planner": lazy(() => import("@/tools/events/ProgrammeShowDayPlanner")),
  "profit-expense-manager": lazy(() => import("@/tools/events/ProfitExpenseManager")),
  "event-report-generator": lazy(() => import("@/tools/events/EventReportGenerator")),
  "rooming-list-planner": lazy(() => import("@/tools/events/RoomingListPlanner")),
  "seating-chart-planner": lazy(() => import("@/tools/events/SeatingChartPlanner")),
  "arrivals-departures-board": lazy(() => import("@/tools/events/ArrivalsDeparturesBoard")),
  "vendor-quote-manager": lazy(() => import("@/tools/events/VendorQuoteManager")),
  "travel-planner": lazy(() => import("@/tools/events/TravelPlanner")),
  "accommodation-manager": lazy(() => import("@/tools/events/AccommodationManager")),
  "seating-floor-planner": lazy(() => import("@/tools/events/SeatingFloorPlanner")),
  "event-tax-summary": lazy(() => import("@/tools/events/EventTaxSummary")),
  "booth-manager": lazy(() => import("@/tools/events/BoothManager")),
  "marketing-materials-manager": lazy(() => import("@/tools/events/MarketingMaterialsManager")),
  "giveaway-branding-manager": lazy(() => import("@/tools/events/GiveawayBrandingManager")),
  "event-suite": lazy(() => import("@/tools/events/EventSuite")),
  "giveaway-winner-picker": lazy(() => import("@/tools/events/GiveawayWinnerPicker")),
  "focus-stage-timer": lazy(() => import("@/tools/events/FocusStageTimer")),
  "agenda-builder": lazy(() => import("@/tools/events/AgendaBuilder")),
  "run-of-show-cue-sheet": lazy(() => import("@/tools/events/RunOfShowCueSheet")),
  "event-day-schedule": lazy(() => import("@/tools/events/EventDaySchedule")),
  // Animation & Avatars
  "emoji-picker": lazy(() => import("@/tools/animation/EmojiPicker")),
  "emoji-unicode-converter": lazy(
    () => import("@/tools/animation/EmojiUnicodeConverter")
  ),
  "avatar-builder": lazy(() => import("@/tools/animation/AvatarBuilder")),
  "sprite-sheet-tool": lazy(() => import("@/tools/animation/SpriteSheetTool")),
  // Household & Family
  "monthly-budget-planner": lazy(
    () => import("@/tools/household/MonthlyBudgetPlanner")
  ),
  "grocery-list-planner": lazy(
    () => import("@/tools/household/GroceryListPlanner")
  ),
  "shopping-list-planner": lazy(
    () => import("@/tools/household/ShoppingListPlanner")
  ),
  "inventory-tracker": lazy(() => import("@/tools/household/InventoryTracker")),
  // College & Education
  "college-cost-calculator": lazy(
    () => import("@/tools/college/CollegeCostCalculator")
  ),
  "student-loan-calculator": lazy(
    () => import("@/tools/college/StudentLoanCalculator")
  ),
  "college-529-calculator": lazy(
    () => import("@/tools/college/College529Calculator")
  ),
  "citation-generator": lazy(() => import("@/tools/college/CitationGenerator")),
  "text-similarity-checker": lazy(
    () => import("@/tools/college/TextSimilarityChecker")
  ),
  "paper-format-checker": lazy(
    () => import("@/tools/college/PaperFormatChecker")
  ),
  // Relocation & Moving
  "moving-cost-estimator": lazy(
    () => import("@/tools/relocation/MovingCostEstimator")
  ),
  "relocation-checklist-planner": lazy(
    () => import("@/tools/relocation/RelocationChecklistPlanner")
  ),
  "cost-of-living-comparison": lazy(
    () => import("@/tools/relocation/CostOfLivingComparison")
  ),
  // Marketing & Print Design
  "menu-card-maker": lazy(() => import("@/tools/marketing/MenuCardMaker")),
  "invitation-card-maker": lazy(
    () => import("@/tools/marketing/InvitationCardMaker")
  ),
  "brochure-flyer-maker": lazy(
    () => import("@/tools/marketing/BrochureFlyerMaker")
  ),
  "banner-sign-maker": lazy(() => import("@/tools/marketing/BannerSignMaker")),
  // a future product line
  "ai-chat": lazy(() => import("@/tools/ai/AiChat")),
  "doc-summarizer": lazy(() => import("@/tools/ai/DocSummarizer")),
  "smart-encoder": lazy(() => import("@/tools/ai/SmartEncoder")),
  "meme-video-generator": lazy(() => import("@/tools/ai/MemeVideoGenerator")),
  "fbx-converter": lazy(() => import("@/tools/ai/FbxConverter")),
  "content-generator": lazy(() => import("@/tools/ai/ContentGenerator")),
  "ai-workflows": lazy(() => import("@/tools/ai/AiWorkflows")),
  "image-captioner": lazy(() => import("@/tools/ai/ImageCaptioner")),
  brainstorm: lazy(() => import("@/tools/ai/Brainstorm")),
  "ai-rewriter": lazy(() => import("@/tools/ai/AiRewriter")),
  "smart-processor": lazy(() => import("@/tools/ai/SmartProcessor")),
};

function ToolSkeleton() {
  return (
    <div className={`space-y-4 ${TOOL_SKELETON_RESERVE_CLASS}`}>
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-96" />
      <Skeleton className="h-64 w-full rounded-xl" />
    </div>
  );
}

export default function ToolPage() {
  const { id, pair: pairSlug } = useParams<{ id: string; pair?: string }>();
  // A third segment is only valid for the unit converter's pair landing pages (B-0109); anywhere else it is a Not Found.
  const unitPair = id === "unit-converter" ? pairBySlug(pairSlug) : undefined;
  const { info: toolInfo, Provider: ToolInfoProvider } = useToolInfoState(id || "");
  const toolRaw = getToolById(id || "");
  // Hidden tools (e.g. retired a future product line) must not be reachable via SEO or direct URL
  const tool = toolRaw && !toolRaw.hidden && (!pairSlug || unitPair) ? toolRaw : undefined;
  const { recordVisit } = useRecentTools();
  const { locale, pathInLocale, localizedHref, strings } = useLocale();

  useEffect(() => {
    if (tool) recordVisit(tool.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool?.id]);
  // A result carried over with "Continue with..." belongs to one tool; opening any other tool drops it.
  useEffect(() => {
    if (tool?.id) dropHandoffUnless(tool.id);
  }, [tool?.id]);
  // Leaving the tool's page ends its usage session (one tool session = one use, see lib/usageGate.ts).
  useEffect(() => {
    const toolId = tool?.id;
    return () => {
      if (toolId) endToolSession(toolId);
    };
  }, [tool?.id]);
  const category = tool ? getCategoryById(tool.category) : undefined;
  const toolName = tool ? getToolName(locale, tool.id, tool.name) : "";
  const categoryName = category ? getCategoryName(locale, category.id, category.name) : "";
  // On a locale page whose copy the server wrote into the HTML, keep that title, meta, H1 and FAQ (B-0123).
  const serverCopy = getServerLocaleCopy(tool?.id, locale);
  // The unit converter's conversion pages (English only) carry their own title, meta description and H1.
  const pairCopy = unitPair ? { title: pairTitle(unitPair), description: pairMeta(unitPair), h1: pairH1(unitPair) } : undefined;
  const faqEntries = tool ? getFaqForTool(tool.id) : undefined;
  const featuredFaq = tool && FEATURED_APP_IDS.has(tool.id) ? FEATURED_APP_CONTENT[tool.id]?.faq : undefined;
  const faqSchema = applyServerFaq(faqEntries, serverCopy).schema;
  const faqVisible = applyServerFaq(featuredFaq ?? faqEntries, serverCopy).visible;
  const isFeaturedApp = !!tool && FEATURED_APP_IDS.has(tool.id);
  const offering = tool && isWorkflowProduct(tool.id) ? { name: "Workflow Products", path: "/workflow-products" } : isFeaturedApp ? { name: "Premium Applications", path: "/premium-apps" } : null;
  // Now applies to every tool, not just the 6 original MultiTool
  // Apps -- every tool page has its own scoped, installable manifest
  // (see scripts/generate-tool-manifests.mjs and usePwaManifest.ts's
  // own doc comment for the full mechanism), so "Install" from any
  // tool's page installs just that one tool as its own standalone app,
  // not the whole 300+ tool site.
  usePwaManifest(tool ? tool.id : null);

  usePageMeta({
    title: tool ? pairCopy?.title ?? serverCopy?.title ?? getSeoTitle(tool.id, toolName) : "Tool Not Found",
    description: tool
      ? pairCopy?.description ?? serverCopy?.description ?? // "Fast, right in your browser" is accurate for the
        // other 300+ tools here, which genuinely run entirely
        // client-side -- it stopped being accurate for this one
        // specifically once Preview/Pre-flight/Download started
        // genuinely sending content server-side for real typesetting
        // (see epubExport.ts and shunnManuscriptDocx.ts's own doc
        // comments in utility-api for why). tool.description alone
        // is accurate on its own and doesn't need that suffix.
        getToolMetaDescription(tool.id, tool.description, {
          premium: isPremiumApp(tool.id),
          suffix: tool.id !== "book-writing-toolbox",
        })
      : undefined,
    // Every other tool page falls back to the generic sitewide
    // og-default.png -- a real, targeted change for every one of
    // them is a much bigger, separate piece of work, but this one
    // already has a real, relevant image sitting unused for exactly
    // this purpose (the launch post's own cover), so using it here
    // is a small, low-risk win rather than leaving a real image on
    // the table for no reason.
    image: tool?.id === "book-writing-toolbox" ? "/blog/book-maker-launch.webp" : undefined,
    path: pathInLocale(locale),
    localized: true,
    // Unknown / removed tool IDs previously returned HTTP 200 "Tool Not Found"
    // which Search Console reports as soft 404. noindex stops indexing; server
    // serveStatic also returns 404 when no prerendered tool HTML exists.
    noindex: !tool || !category,
  });

  useStructuredData(
    tool
      ? {
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: toolName,
          description: tool.description,
          applicationCategory: "UtilitiesApplication",
          operatingSystem: "Any (Web Browser)",
          browserRequirements: "Requires JavaScript",
          url: `https://www.freedailypro.com/tool/${tool.id}${unitPair ? `/${unitPair.slug}` : ""}`,
          offers: {
            "@type": "Offer",
            // Workflow products and premium apps are paid after the trial uses;
            // advertise the monthly price so the data matches the page.
            price: isWorkflowProduct(tool.id) ? "29.99" : isPremiumApp(tool.id) ? "9.99" : "0",
            priceCurrency: "USD",
            // Workflow Products (Book Writing Toolbox included) are a $29.99 monthly subscription with a 7-day trial.
            ...(isWorkflowProduct(tool.id) && tool.id !== "event-suite"
              ? {
                  description: "$29.99 a month with a 7-day free trial.",
                  priceSpecification: { "@type": "UnitPriceSpecification", price: "29.99", priceCurrency: "USD", billingDuration: 1, billingIncrement: 1, unitCode: "MON" },
                }
              : {}),
          },
        }
      : null
  );

  // YouTube how-to video (when mapped in tool-videos.ts)
  useStructuredData(
    (() => {
      if (!tool) return null;
      const v = getToolVideo(tool.id);
      if (!v) return null;
      return {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: videoDisplayTitle(v.title),
        description: `${toolName}. ${tool.description}`.slice(0, 300),
        thumbnailUrl: `https://i.ytimg.com/vi/${v.videoId}/hqdefault.jpg`,
        contentUrl: `https://www.youtube.com/watch?v=${v.videoId}`,
        embedUrl: `https://www.youtube.com/embed/${v.videoId}`,
        uploadDate: v.uploadDate ?? "2026-08-22",
        publisher: {
          "@type": "Organization",
          name: "FreeDailyPro",
          url: "https://www.freedailypro.com",
        },
      };
    })()
  );

  useStructuredData(
    faqSchema && faqSchema.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqSchema.map(entry => ({
            "@type": "Question",
            name: entry.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: entry.source ? `${entry.answer} Official source: ${entry.source.label} (${entry.source.url})` : entry.answer,
            },
          })),
        }
      : null
  );

  useStructuredData(
    tool && category
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.freedailypro.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: offering ? offering.name : categoryName || category.name,
              item: offering ? `https://www.freedailypro.com${offering.path}` : `https://www.freedailypro.com/category/${category.id}`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: toolName || tool.name,
              item: `https://www.freedailypro.com/tool/${tool.id}`,
            },
          ],
        }
      : null
  );

  if (!tool || !category) {
    return (
      <div className="container py-20 text-center">
        <h1 className="text-2xl font-bold mb-4">Tool Not Found</h1>
        <Link href="/">
          <Button variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
          </Button>
        </Link>
      </div>
    );
  }

  const ToolComponent = toolComponents[tool.id];

  return (
    <div className="page-enter">
      {/* Breadcrumb */}
      <section className="border-b border-border/50 bg-muted/20">
        <div className="container py-4 md:py-6">
          <div className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground mb-2">
            <Link href="/" className="hover:text-foreground transition-colors">
              Home
            </Link>
            <span>/</span>
            <Link
              href={offering ? localizedHref(offering.path) : localizedHref(`/category/${category.id}`)}
              className="hover:text-foreground transition-colors"
            >
              {offering ? offering.name : categoryName}
            </Link>
            <span>/</span>
            {unitPair ? (
              <>
                <Link href="/tool/unit-converter" className="hover:text-foreground transition-colors">{toolName}</Link>
                <span>/</span>
                <span className="text-foreground font-medium">{unitPair.label}</span>
              </>
            ) : (
              <span className="text-foreground font-medium">{toolName}</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
              {tool ? pairCopy?.h1 ?? serverCopy?.h1 ?? getSeoH1(tool.id, locale, toolName) : toolName}
            </h1>
            <FavoriteButton toolId={tool.id} className="mt-1" />
            <ShareButton title={toolName} className="mt-1" />
            <InstallToolButton toolName={toolName} className="mt-1" />
            <RegionBadge region={tool.region} />
          </div>
          <p className={`text-muted-foreground mt-1 ${AUTO_ADS_EXCLUDE_CLASS}`}>{tool.description}</p>
          <ToolTitleBadges toolId={tool.id} />
          <UsageAndUpgradeStrip />
        </div>
      </section>

      {/* Tool Content */}
      <section className="container py-6 md:py-8 relative">
        <SideRailAd />
        {ToolComponent ? (
          <>
            {/* Book Writing Toolbox skips the marketing content below
                (hero, how-to-use, tips, FAQ) -- not because there isn't
                enough product to explain anymore (there genuinely is,
                see the real TOOL_EDITORIAL_CONTENT and FAQ_CONTENT
                entries written for it), but because rendering that much
                long-form text directly below a real, already-complex
                editor read as a second, unrelated page bolted onto the
                bottom of the tool -- real feedback: "it is like another
                book :) which will make website look ugly". That same
                content now lives inside the tool itself, behind a real
                Help tab in BookMaker.tsx's own sidebar, reachable
                on-demand rather than always taking up page real estate
                whether or not anyone wants to read it right then. */}
            {isFeaturedApp && tool && tool.id !== "book-writing-toolbox" && FEATURED_APP_CONTENT[tool.id] && (
              <FeaturedAppHero appName={toolName} content={FEATURED_APP_CONTENT[tool.id]} />
            )}
            <ToolInfoProvider>
              <DownloadGateRegistrar />
              {/* Tool area: automatic ads and in-text annotation chips stay out of it (ads remain below the result). */}
              <div className={`tool-area ${AUTO_ADS_EXCLUDE_CLASS}`} data-testid="tool-area">
                <Suspense fallback={<ToolSkeleton />}>
                  <ToolComponent />
                </Suspense>
              </div>
            </ToolInfoProvider>
            {/* Ad unit 1 of 2: directly below the tool (after its result area), never above or between its inputs. */}
            <ToolResultAd />
            <AfterDownloadAd />
            {/* One layout for every tool, below the tool: About + video, then
                collapsed Common questions / Details / Guides. The tool files
                hand their About text to this panel (ToolInfoContext), so the
                position is identical whichever tool or layout is used. The
                Book Writing Toolbox keeps its own internal structure (its
                Help tab) but gets the same panel below it. */}
            <ToolInfoPanel
              toolId={tool.id}
              toolName={toolName}
              description={tool.description}
              info={toolInfo}
              faq={faqVisible}
              editorial={TOOL_EDITORIAL_CONTENT[tool.id]}
              extraDetails={
                isFeaturedApp && FEATURED_APP_CONTENT[tool.id] ? (
                  <>
                    <div>
                      <h3 className="text-sm font-semibold mb-1">How to use this app</h3>
                      <ol className="space-y-2 text-sm text-muted-foreground list-decimal pl-5">
                        {FEATURED_APP_CONTENT[tool.id].howToUse.map((step, i) => <li key={i}>{step}</li>)}
                      </ol>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold mb-1">Tips</h3>
                      <ul className="space-y-2 text-sm text-muted-foreground list-disc pl-5">
                        {FEATURED_APP_CONTENT[tool.id].tips.map((t, i) => <li key={i}>{t}</li>)}
                      </ul>
                    </div>
                  </>
                ) : undefined
              }
            />
            {/* Ad unit 2 of 2: lower on the page, after the how-to / questions area. */}
            <InlineAd />
            <ToolEngagementFooter />
          </>
        ) : (
          <div className="text-center py-16 border border-dashed border-border rounded-xl">
            <p className="text-muted-foreground">This tool is coming soon.</p>
            <Link href={localizedHref(`/category/${category.id}`)}>
              <Button variant="outline" className="mt-4">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to {categoryName}
              </Button>
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
